import { isRequestError } from 'utils/requestError';
import { buildUpdateUserPayload } from 'utils/userPayload';
import React, { useCallback, useEffect } from 'react';
import { Button, InputWraper, useForm } from 'modules';
import * as Yup from 'yup';
import {
  useGetUserInfoQuery,
  useUpdateUserMutation,
  useGetCompanyAllQuery,
  useGetScreenActionPermissionsQuery,
} from 'services';
import {
  InputText,
  InputSelect,
  InputGroupedCheckbox,
  normalizeGroupedCheckboxValues,
  Loading,
} from 'components';
import toast from 'react-hot-toast';

import { UpdateUserSuccess, UpdateUserSuccessDefined } from './configs';
import { getUserRoleOptions } from '../list/configs';
import { InputToggleSwitch } from '../../../components/toggle-switch/index';
import { Roles } from 'configs/roles';
import { ScreenActionPermissionItem } from 'services/openapi';
import { getUserRoles } from 'utils/helpers';
import { BidvVaInput } from '../BidvVaInput';

interface UpdateUserProps {
  userId: string;
  onClose: () => void;
  forceUpdateTable: () => void;
  refetchLeadersList: () => void;
}

const UpdateForm = ({
  onClose,
  forceUpdateTable,
  userId,
  refetchLeadersList,
}: UpdateUserProps) => {
  const roles = getUserRoles();
  const {
    data: userInfo,
    isFetching: isFetchingUserInfo,
    refetch: refetchUserInfo,
  } = useGetUserInfoQuery({ userId });
  const dataForm = userInfo?.data as any;
  const [isUserActive, setIsUserActive] = React.useState<boolean>(
    dataForm?.isActive ?? false,
  );
  const {
    mutateAsync,
    data: dataUpdateUser,
    error: errorUpdateUser,
  } = useUpdateUserMutation();
  const { data: companyInfo } = useGetCompanyAllQuery();
  const { data: dataScreenActionPermissions } =
    useGetScreenActionPermissionsQuery({
      enabled: roles.includes(Roles.SUPER_ADMIN),
    });

  const {
    phoneNumber,
    fullName,
    email,
    role,
    compId,
    accountNumber,
    bidvVaPayerId,
    citizenIdentityCardNo,
    objectType,
    adminActions,
  } = dataForm || {};

  const dataScreenActionPermissionsLst = useCallback(() => {
    if (!dataScreenActionPermissions) return [];
    return dataScreenActionPermissions?.data?.map(
      (item: ScreenActionPermissionItem) => ({
        label: item.value,
        value: item.key,
      }),
    );
  }, [dataScreenActionPermissions]);

  const normalizedPhoneNumber = phoneNumber
    ? String(phoneNumber).replace(/\+84/, '0')
    : '';

  useEffect(() => {
    setIsUserActive(dataForm?.isActive ?? false);
  }, [dataForm]);

  const structure = () => {
    return [
      {
        name: 'phoneNumber',
        defaultValue: normalizedPhoneNumber,
        isDisabled: true,
        label: 'Số điện thoại',
        validate: Yup.string()
          .required('Vui lòng nhập số điện thoại')
          .matches(
            /^0\d{9}$/,
            'Số điện thoại không hợp lệ, vui lòng nhập số điện thoại bắt đầu bằng số 0 và có 10 chữ số',
          ),
      },
      {
        name: 'fullName',
        defaultValue: fullName,
        label: 'Tài khoản',
        validate: Yup.string().required('Vui lòng nhập tên tài khoản'),
      },
      {
        name: 'citizenIdentityCardNo',
        defaultValue: citizenIdentityCardNo,
        label: 'CCCD/MST',
        validate: Yup.string()
          .required('Vui lòng nhập CCCD/MST')
          .matches(
            /^\d{6,}$/,
            'CCCD/MST không hợp lệ, vui lòng nhập tối thiểu 6 số',
          ),
      },
      {
        name: 'accountNumber',
        defaultValue: accountNumber,
        isDisabled: true,
        label: 'Tiểu khoản TPBank',
      },
      {
        name: 'bidvVaPayerId',
        defaultValue: bidvVaPayerId,
        isDisabled: bidvVaPayerId ? true : false,
        label: 'Tiểu khoản BIDV',
        validate: Yup.string()
          .nullable()
          .max(16, 'Tiểu khoản BIDV không được vượt quá 16 ký tự')
          .matches(/^[A-Z0-9]+$/, {
            message: 'Tiểu khoản BIDV phải viết hoa và chỉ chứa chữ cái và số',
            excludeEmptyString: true,
          }),
      },
      {
        name: 'email',
        defaultValue: email,
        label: 'Email',
        validate: Yup.string().email('Email không hợp lệ').nullable(),
      },
      {
        name: 'role',
        defaultValue: role === 'Agency' ? objectType : role,
        label: 'Vai trò',
        validate: Yup.string().required('Vui lòng chọn vai trò'),
      },
      {
        name: 'adminActions',
        defaultValue: adminActions,
        // isDisabled: role !== Roles.ADMIN,
        label: 'Thao tác',
      },
      {
        name: 'compId',
        defaultValue: compId,
        isDisabled: Boolean(compId),
        label: 'Doanh nghiệp/HTX',
        // validate: Yup.string()
        //   .when('role', {
        //     is: 'Leader',
        //     then: Yup.string().required('Vui lòng chọn PC'),
        //     otherwise: Yup.string().nullable(),
        //   })
        //   .test({
        //     test: function (value) {
        //       const role = this.resolve(Yup.ref('role'));
        //       if (role === 'SpVisor' || value) {
        //         return true;
        //       }
        //       return false;
        //     },
        //     message: 'Trường này chỉ được bỏ trống khi vai trò là Nhân viên đối soát',
        //   }),
      },
      // {
      //   name: 'agencyId',
      //   defaultValue: agencyId,
      //   label: 'Phòng ban',
      //   // validate: Yup.string()
      //   //   .when('role', {
      //   //     is: 'Agency',
      //   //     then: Yup.string().required('Vui lòng chọn Điện lực'),
      //   //     otherwise: Yup.string().nullable(),
      //   //   })
      //   //   .test({
      //   //     test: function (value) {
      //   //       const role = this.resolve(Yup.ref('role'));
      //   //       if (role === 'SpVisor' || role === 'Leader' || value) {
      //   //         return true;
      //   //       }
      //   //       return false;
      //   //     },
      //   //     message: 'Trường này chỉ được bỏ trống khi vai trò là Nhân viên đối soát hoặc Trưởng nhóm',
      //   //   }),
      // },
    ];
  };

  const { handleSubmit, getInputProps, resetForm, setValues } = useForm({
    enableReinitialize: true,
    structure: structure(),
    onSubmit: async (values) => {
      const normalizedPhoneNumber = values.phoneNumber.replace(/^0/, '+84');
      const normalizedValues = {
        ...values,
        compId: compId || values.compId,
        phoneNumber: normalizedPhoneNumber,
        userId: userId,
        isActive: isUserActive,
      };

      if (normalizedValues.role !== Roles.ADMIN) {
        normalizedValues.adminActions = [];
      } else {
        normalizedValues.adminActions = normalizeGroupedCheckboxValues(
          normalizedValues.adminActions,
          dataScreenActionPermissionsLst(),
        );
      }
      try {
        await mutateAsync(
          buildUpdateUserPayload({
            ...normalizedValues,
            objectType: normalizedValues.objectType || objectType,
          }),
        );
        resetForm();
        refetchLeadersList();
        refetchUserInfo();
        forceUpdateTable();
        onClose();
      } catch (error: any) {
        if (!isRequestError(error)) {
          toast.error(error.message || 'Không thể cập nhật người dùng');
        }
      }
    },
  });

  const vaPrefix =
    companyInfo?.find((item) => item.compId === getInputProps('compId').value)
      ?.bidvVaPrefix || '';

  const dataCompany =
    companyInfo
      // ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
      ?.map((item) => ({ label: `${item.compName}`, value: item.compId })) ||
    [];

  const dataCompanyWithRoleAgency =
    companyInfo
      // ?.filter((item) => Object.keys(item.compUser).length !== 0)
      // ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];
      ?.map((item) => ({ label: `${item.compName}`, value: item.compId })) ||
    [];

  // const dataCompanyWithRoleLeader =
  //   companyInfo
  //     // ?.filter((item) => Object.keys(item.compUser).length === 0 || item.compId === compId)
  //     ?.map((item) => ({ label: `${item.compName}- ${item.compId}`, value: item.compId })) || [];

  let checkedDataCompany: any = [];

  switch (getInputProps('role').value) {
    case 'Agency':
      checkedDataCompany = dataCompanyWithRoleAgency;
      break;

    // case 'Leader':
    //   checkedDataCompany = dataCompanyWithRoleLeader;
    //   break;

    default:
      checkedDataCompany = dataCompany;
      break;
  }

  useEffect(() => {
    if (dataUpdateUser) {
      const { statusCode } = dataUpdateUser;

      const dataDefined =
        UpdateUserSuccessDefined[statusCode as UpdateUserSuccess];

      if (statusCode === 200) {
        toast.success(dataDefined?.message, { duration: 1500 });
      }
    }

    // if (errorUpdateUser) {
    //   const { status } = errorUpdateUser as Error;
    //   toast.error(UpdateUserErrorDefined[(status as UpdateUserError) || 500]?.message);
    // }
  }, [dataUpdateUser, errorUpdateUser]);

  return (
    <Loading isLoading={isFetchingUserInfo && !userInfo}>
      <div className='container-fluid Form__Updateuser'>
        <div className='row wrap-form-update-user'>
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('phoneNumber')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('fullName')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('citizenIdentityCardNo')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('accountNumber')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6 bidv-va-input'
            inputProps={getInputProps('bidvVaPayerId')}
            renderInput={(props) => (
              <BidvVaInput {...props} vaPrefix={vaPrefix} />
            )}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('email')}
            component={InputText}
          />
          <InputWraper
            className='col-lg-6'
            inputProps={getInputProps('role')}
            renderInput={(props) => (
              <InputSelect
                {...props}
                onChange={(e) => {
                  if (e.value === Roles.ADMIN) {
                    setValues({ adminActions: [] });
                    if (!compId) {
                      setValues({ compId: '' });
                    }
                  }
                  props.onChange(e);
                }}
                options={getUserRoleOptions(roles)}
              />
            )}
          />
          {getInputProps('role').value === Roles.ADMIN && (
            <InputWraper
              className='col-12'
              inputProps={getInputProps('adminActions')}
              renderInput={(props) => (
                <InputGroupedCheckbox
                  {...props}
                  options={dataScreenActionPermissionsLst()}
                  onResetAgency={() => setValues({ agencyId: '' })}
                />
              )}
            />
          )}
          {getInputProps('role').value !== Roles.SUPER_ADMIN &&
            getInputProps('role').value !== Roles.ADMIN && (
              <InputWraper
                className='col-lg-6'
                inputProps={getInputProps('compId')}
                renderInput={(props) => (
                  <InputSelect
                    {...props}
                    onChange={(e) => {
                      setValues({ agencyId: '' });
                      props.onChange(e);
                    }}
                    options={checkedDataCompany}
                  />
                )}
              />
            )}
          {/* {getInputProps('compId').value && getInputProps('role').value !== 'Leader' && (
            <InputWraper
              className="col-lg-6"
              inputProps={getInputProps('agencyId')}
              renderInput={(props) => <InputSelect {...props} options={dataAgency} />}
            />
          )} */}
        </div>
        <div className='input'>
          <div className='label'>Trạng thái</div>
          <InputToggleSwitch
            onChange={async (value) => setIsUserActive(value)}
            defaultValue={isUserActive}
            value={isUserActive}
            onTouched={() => false}
            label=''
            name=''
          />
        </div>
      </div>
      <div className='Button__update--user'>
        <Button label='Cập nhật' buttonType='success' onClick={handleSubmit} />
        <Button label='Trở về' buttonType='grey' onClick={() => onClose()} />
      </div>
    </Loading>
  );
};

export default UpdateForm;
